import RequestAbstract from "./RequestAbstract";

/**
 * AI Calendar API Client
 * Handles multi-post content planning and generation
 */
export default class AICalendar extends RequestAbstract {
  get link() {
    return this.links.accounts + "/ai-calendar";
  }

  /**
   * POST /ai-calendar/analyze-goals
   * Step 1: Analyze user's goals and generate content strategy
   * 
   * @param {Object} params - Analysis parameters
   * @param {string[]} params.objectives - Array of objectives: 'brand_awareness', 'engagement', 'conversions', 'traffic', 'community_building'
   * @param {Object} params.target_metrics - Target metrics { reach?, engagement_rate?, conversions? }
   * @param {Object} params.time_period - { type: 'week'|'month', start_date: '2026-04-01', end_date?: '2026-04-30' }
   * @param {Object} params.business_context - { industry, target_audience, brand_voice?: 'professional'|'casual'|'friendly', account_ids?: [] }
   * @returns {Promise} Strategy with posting frequency, content mix, platforms, best times, recommendations
   */
  async analyzeGoals(params) {
    return await this.post(this.link + "/analyze-goals", params);
  }

  /**
   * POST /ai-calendar/suggest-topics
   * Step 2: Get AI-suggested content topics based on strategy
   * 
   * @param {Object} params - Topic suggestion parameters
   * @param {Object} params.strategy - Strategy object from analyzeGoals response
   * @param {number[]} params.account_ids - Array of account IDs
   * @param {string[]} params.custom_topics - Optional custom topics to include
   * @param {string[]} params.avoid_topics - Optional topics to avoid
   * @param {string} params.language - Language code (e.g., 'en', 'de')
   * @returns {Promise} Array of suggested topics with categories, priorities, keywords
   */
  async suggestTopics(params) {
    return await this.post(this.link + "/suggest-topics", params);
  }

  /**
   * POST /ai-calendar/optimize-schedule
   * Step 3: Optimize posting schedule based on topics and constraints
   * 
   * @param {Object} params - Schedule optimization parameters
   * @param {Array} params.topics - Array of Topic objects from suggestTopics
   * @param {Object} params.time_period - { start_date: '2026-04-01', end_date: '2026-04-30' }
   * @param {number[]} params.account_ids - Array of account IDs
   * @param {Object} params.constraints - Optional constraints { min_days_between_similar?, avoid_weekends?, preferred_times? }
   * @param {string} params.user_timezone - User timezone (e.g., 'Europe/Berlin')
   * @returns {Promise} Optimized schedule with daily slots and summary
   */
  async optimizeSchedule(params) {
    return await this.post(this.link + "/optimize-schedule", params);
  }

  /**
   * POST /ai-calendar/generate-posts
   * Step 4: Generate actual post content (async job)
   * 
   * @param {Object} params - Post generation parameters
   * @param {Array} params.schedule - Schedule array from optimizeSchedule
   * @param {Array} params.topics - Topics array from suggestTopics
   * @param {Object} params.business_context - Business context from Step 1
   * @param {Object} params.generation_settings - { creativity: 0-1, include_hashtags: true/false, include_emoji: true/false, language: 'en', content_length?: 'short'|'medium'|'long' }
   * @param {number[]} params.account_ids - Array of account IDs
   * @returns {Promise} Job info with job_id, status_url, estimated_time_seconds
   */
  async generatePosts(params) {
    return await this.post(this.link + "/generate-posts", params);
  }

  /**
   * GET /ai-calendar/generation-status/:jobId
   * Poll generation status (call repeatedly until completed/failed)
   * 
   * @param {string} jobId - Job ID from generatePosts response
   * @returns {Promise} Status object { status: 'processing'|'completed'|'failed', progress?, posts_generated?, posts_total?, posts?, error? }
   */
  async getGenerationStatus(jobId) {
    return await this.get(this.link + "/generation-status/" + jobId);
  }

  /**
   * POST /ai-calendar/finalize
   * Step 6: Finalize and save posts to schedule
   * 
   * @param {Object} params - Finalization parameters
   * @param {Array} params.posts - Array of posts to finalize (from generation or edited)
   * @param {string} params.calendar_name - Optional calendar name
   * @param {number} params.draft_collection_id - Optional draft collection ID (save as drafts instead)
   * @returns {Promise} Created posts info with IDs, summary
   */
  async finalize(params) {
    return await this.post(this.link + "/finalize", params);
  }
}
